import React from 'react'
import { Tabs } from 'components/FbUI/Tabs'
import { formatInteger } from 'utils/formatting'
import { UnifiedSearchTable } from './UnifiedSearchTable'
import { UnifiedSearchVariantTable } from './UnifiedSearchVariantTable'
import { UnifiedSearchEmptyState } from './UnifiedSearchEmptyState'
import { ContactCompanySearchResult, DoorSearchResult } from 'models/companies'
import { VariantSearchResult } from 'services/search_api'
import { UnifiedSearchTab } from './constants'
import { SearchResultUnion } from './hooks/useUnifiedSearchAPI'

// Type for non-variant search results (used by UnifiedSearchTable)
type NonVariantSearchResult = ContactCompanySearchResult | DoorSearchResult

// Format count with "+" indicator if at the 10,000 cap
const formatCountWithCap = (count: number): string => {
  if (count === 10000) {
    return '10,000+'
  }
  return formatInteger(count)
}

interface UnifiedSearchResultsProps {
  selectedTab: UnifiedSearchTab
  setSelectedTab: (tab: UnifiedSearchTab) => void
  firstBiteCount: number
  unmatchedCount: number
  firstBiteDoorCount: number
  unmatchedDoorCount: number
  variantCount: number
  variantResults: VariantSearchResult[]
  currentResults: SearchResultUnion[] // Now properly typed instead of any[]
  debouncedSearchTerm: string
  isLoading: boolean
  currentTotalRows: number
  pageSize: number
  currentPage: number
  setCurrentPage: (page: number) => void
  onRowClick: (row: SearchResultUnion) => void
  selectedItem?: SearchResultUnion | null
  showRadioButtons?: boolean
  onVariantDataChange?: () => void
  /** When provided, only tabs whose key is in this array are shown. */
  visibleTabs?: UnifiedSearchTab[]
}

export function UnifiedSearchResults(props: UnifiedSearchResultsProps) {
  const {
    selectedTab,
    setSelectedTab,
    firstBiteCount,
    unmatchedCount,
    firstBiteDoorCount,
    unmatchedDoorCount,
    variantCount,
    variantResults,
    currentResults,
    debouncedSearchTerm,
    isLoading,
    currentTotalRows,
    pageSize,
    currentPage,
    setCurrentPage,
    onRowClick,
    selectedItem,
    showRadioButtons = false,
    onVariantDataChange,
    visibleTabs,
  } = props
  const handleTabChange = (tab: string) => {
    setSelectedTab(tab as UnifiedSearchTab)
  }

  // Check for results based on current tab
  const hasResults =
    selectedTab === 'variants'
      ? variantResults.length > 0
      : currentResults.length > 0
  const shouldShowTable = debouncedSearchTerm && (hasResults || isLoading)
  const shouldShowEmptyState =
    !debouncedSearchTerm || (debouncedSearchTerm && !isLoading && !hasResults)

  return (
    <div className="flex-1">
      {/* Tabs - always show the tabs */}
      <div className="px-3">
        <Tabs
          tabs={[
            {
              key: 'firstbite',
              title: `FB Companies (${formatCountWithCap(firstBiteCount)})`,
            },
            {
              key: 'firstbitedoors',
              title: `FB Locations (${formatCountWithCap(firstBiteDoorCount)})`,
            },
            {
              key: 'unmatched',
              title: `Custom Companies (${formatCountWithCap(unmatchedCount)})`,
            },
            {
              key: 'unmatcheddoors',
              title: `Custom Locations (${formatCountWithCap(unmatchedDoorCount)})`,
            },
            {
              key: 'variants',
              title: `Product Variants (${formatCountWithCap(variantCount)})`,
            },
          ].filter(
            (tab) =>
              !visibleTabs || visibleTabs.includes(tab.key as UnifiedSearchTab)
          )}
          selectedTab={selectedTab}
          setSelectedTab={handleTabChange}
          style={{ borderBottom: 'none' }}
        />
      </div>

      {/* Results */}
      <div className={`overflow-hidden flex flex-col h-[510px]`}>
        {/* Show empty state only when not loading and no results */}
        {shouldShowEmptyState && (
          <UnifiedSearchEmptyState
            debouncedSearchTerm={debouncedSearchTerm}
            isLoading={isLoading}
            selectedTab={selectedTab}
            hasResults={hasResults}
          />
        )}

        {/* Show table when we have a search term and either have results or are loading */}
        {shouldShowTable &&
          (selectedTab === 'variants' ? (
            <UnifiedSearchVariantTable
              data={variantResults}
              isLoading={isLoading}
              currentTotalRows={currentTotalRows}
              pageSize={pageSize}
              currentPage={currentPage}
              setCurrentPage={setCurrentPage}
              onDataChange={onVariantDataChange}
            />
          ) : (
            <UnifiedSearchTable
              data={currentResults as NonVariantSearchResult[]}
              isLoading={isLoading}
              selectedTab={
                selectedTab as
                  | 'firstbite'
                  | 'unmatched'
                  | 'firstbitedoors'
                  | 'unmatcheddoors'
              }
              currentTotalRows={currentTotalRows}
              pageSize={pageSize}
              currentPage={currentPage}
              setCurrentPage={setCurrentPage}
              onRowClick={onRowClick as (row: NonVariantSearchResult) => void}
              selectedItem={selectedItem as NonVariantSearchResult | null}
              showRadioButtons={showRadioButtons}
            />
          ))}
      </div>
    </div>
  )
}
